import {
    Animation,
    IAnimationKey,
    Mesh,
    MeshBuilder,
    StandardMaterial,
    Texture,
} from "@babylonjs/core"
import { BabUtils } from "../../BabUtils"
import { MazeAudio, SoundLoader } from "./assets/MazeAudio"
import { mazeImages } from "./assets/MazeImages"
import { MazeGrid } from "./MazeGrid"
import { MazeStore } from "./MazeStore"
import { Sound } from "@babylonjs/core"
import { log } from "../../../common/Logging"

interface MazeRunnerPoint {
    x: number
    z: number
    info?: SmokeInfo
}

/**
 * SmokeInfo's x and z are the location of the cig.
 */
interface SmokeInfo {
    x?: number
    z?: number
    smoke?: boolean
    audio: SoundLoader
    text: string
    cig?: boolean
    ecig?: boolean
}

interface Route {
    animation: Animation
    targetInfo?: SmokeInfo
}

export class MazeRunner {
    private runner: Mesh
    private routes: Route[] = []
    private frameRate = 15

    constructor(
        private user: boolean,
        private targetCoors: Array<MazeRunnerPoint>,
    ) {
        const { scene } = MazeStore.instance

        // Set up runner pawn

        this.runner = MeshBuilder.CreatePlane(
            "runner",
            { width: 8, height: 8 },
            scene,
        )
        const runnerMat = new StandardMaterial("runnerMat", scene)
        const runnerImage = user ? mazeImages.smoker : mazeImages.nonSmoker
        runnerMat.diffuseTexture = new Texture(runnerImage, scene)
        runnerMat.diffuseTexture.hasAlpha = true
        this.runner.material = runnerMat
        this.runner.position.y = 2 + 5 / 6
        const runnerPosition = MazeGrid.getMidPositionOnGrid(targetCoors[0])
        this.runner.position.x = runnerPosition.x
        this.runner.position.z = runnerPosition.z
        this.runner.addRotation(BabUtils.degreeToRadian(90), 0, 0)

        // Set up runner animations

        let routePoints: MazeRunnerPoint[] = []
        const addCurRouteToRoutes = (
            points: MazeRunnerPoint[],
            targetInfo?: SmokeInfo,
        ) => {
            const routeAnimation = new Animation(
                "routeAnimation",
                "position",
                this.frameRate,
                Animation.ANIMATIONTYPE_VECTOR3,
            )
            let prevCoor = {
                x: this.runner.position.x,
                z: this.runner.position.z,
            }
            let distanceFromStart = 0
            const keyFrames: IAnimationKey[] = points.map((coor) => {
                distanceFromStart += BabUtils.manhattanDistance(coor, prevCoor)
                prevCoor = coor
                return {
                    frame: Math.ceil(
                        (distanceFromStart * this.frameRate) / 1.5,
                    ),
                    value: MazeGrid.getMidPositionOnGrid(coor),
                }
            })
            routeAnimation.setKeys(keyFrames)
            this.routes.push({
                animation: routeAnimation,
                targetInfo: targetInfo
                    ? {
                          ...targetInfo,
                          x: prevCoor.x,
                          z: prevCoor.z,
                      }
                    : undefined,
            })
        }

        targetCoors.forEach((coor) => {
            routePoints.push(coor)
            addCurRouteToRoutes(routePoints, coor.info)
            routePoints = [routePoints.pop()]
        })
        if (routePoints.length > 0) {
            addCurRouteToRoutes(routePoints)
        }
    }

    disposeRunner = () => this.runner.dispose()

    run = async () => {
        const { scene } = MazeStore.instance
        const { gui, mazeCigs } = MazeStore.instance
        const { bell, buzzer, timesUpBuzzer, sfxVolume } = MazeAudio.instance
        bell.setVolume(sfxVolume)
        buzzer.setVolume(sfxVolume)
        timesUpBuzzer.setVolume(sfxVolume)

        try {
            gui.startTimer()
            const initPromise = Promise.resolve()

            // Play each async animation in sequence
            await this.routes.reduce(async (prevRoute, curRoute) => {
                await prevRoute
                this.runner.animations.splice(0)
                this.runner.animations.push(curRoute.animation)
                const animationKeys = curRoute.animation.getKeys()
                const lastFrame = animationKeys[animationKeys.length - 1].frame
                const currentAnimation = scene.beginAnimation(
                    this.runner,
                    0,
                    lastFrame,
                )
                await currentAnimation.waitAsync()

                // Animation complete, check conditions
                if (gui.timerSeconds < 1) throw "TIME_UP"
                if (curRoute.targetInfo) {
                    // Has target info: play buzzer, then play audio and display text
                    gui.stopTimer()
                    await MazeAudio.asyncPlaySound(buzzer)
                    const text = gui.addGuiText(curRoute.targetInfo.text)
                    await curRoute.targetInfo.audio()

                    // Audio done, clear text and start timer
                    gui.startTimer()
                    text.dispose()
                    if (curRoute.targetInfo.smoke) {
                        // Maze runner is smoker, add smoke FX and await smoking
                        const { x, z, cig, ecig } = curRoute.targetInfo
                        mazeCigs.addSmokeAt({
                            x,
                            z,
                            cig,
                            ecig,
                            orientHorizontally: true,
                            moveUp: true,
                        })
                        await new Promise((r) => setTimeout(r, 8200))
                        mazeCigs.stopSmoke()
                    }
                }
            }, initPromise)
        } catch (error) {
            log.debug(error)
        } finally {
            // Runner is through maze or time is up
            gui.stopTimer()
            let timeUpSound: Sound
            if (gui.timerSeconds > 0) {
                timeUpSound = bell
            } else {
                gui.timerSeconds = 0
                timeUpSound = timesUpBuzzer
            }
            await MazeAudio.asyncPlaySound(timeUpSound)
        }
    }

    static nonUser = () =>
        new MazeRunner(false, [
            { x: 4, z: 1 },
            { x: 3, z: 1 },
            { x: 3, z: 3 },
            { x: 2, z: 3 },
            { x: 2, z: 2 },
            { x: 0, z: 2 },
            { x: 0, z: 3 },
            {
                x: 1,
                z: 3,
                info: {
                    audio: MazeAudio.instance.nonUser1,
                    text: "Uh-oh. The teen took a wrong turn, but since they don’t vape, they can move on easily.",
                },
            },
            { x: 0, z: 3 },
            { x: 0, z: 2 },
            { x: 2, z: 2 },
            { x: 2, z: 3 },
            { x: 4, z: 3 },
            { x: 4, z: 5 },
            {
                x: 5,
                z: 5,
                info: {
                    audio: MazeAudio.instance.nonUser2,
                    text: "Oops, another wrong turn, but it’s okay. The teen won’t lose any time because they don’t smoke.",
                },
            },
            { x: 3, z: 5 },
            { x: 3, z: 4 },
            { x: 0, z: 4 },
            { x: 0, z: 5 },
            { x: 2, z: 5 },
            { x: 2, z: 6 },
            { x: 4, z: 6 },
            { x: 4, z: 7 },
            { x: 6, z: 7 },
            { x: 6, z: 8 },
            { x: 8, z: 8 },
            { x: 8, z: 7 },
            {
                x: 7,
                z: 7,
                info: {
                    audio: MazeAudio.instance.nonUser3,
                    text: "Oh, no, another e-cigarette! But the teen can just keep going because they don’t need to stop and vape.",
                },
            },
            { x: 8, z: 7 },
            { x: 8, z: 8 },
            { x: 3, z: 8 },
            { x: 3, z: 7 },
            { x: 0, z: 7 },
            { x: 0, z: 9 },
        ])

    static user = () =>
        new MazeRunner(true, [
            { x: 4, z: 1 },
            { x: 6, z: 1 },
            { x: 6, z: 2 },
            { x: 8, z: 2 },
            { x: 8, z: 1 },
            {
                x: 7,
                z: 1,
                info: {
                    smoke: true,
                    audio: MazeAudio.instance.user1,
                    text: "Oh no! The teen ran into a cigarette. They’ll lose time because they have to stop and smoke.",
                    cig: true,
                },
            },
            { x: 8, z: 1 },
            { x: 8, z: 4 },
            { x: 7, z: 4 },
            { x: 7, z: 3 },
            { x: 5, z: 3 },
            { x: 5, z: 2 },
            {
                x: 4,
                z: 2,
                info: {
                    smoke: true,
                    audio: MazeAudio.instance.user2,
                    text: "Wrong turn! The teen is having a hard time getting out of the maze.",
                    ecig: true,
                },
            },
            { x: 5, z: 2 },
            { x: 5, z: 4 },
            { x: 6, z: 4 },
            { x: 6, z: 6 },
            { x: 7, z: 6 },
            { x: 7, z: 5 },
            { x: 8, z: 5 },
            {
                x: 8,
                z: 6,
                info: {
                    smoke: true,
                    audio: MazeAudio.instance.user3,
                    text: "Not again! The teen has to stop to smoke another cigarette.",
                    cig: true,
                },
            },
            { x: 8, z: 5 },
            { x: 7, z: 5 },
            { x: 7, z: 6 },
            { x: 2, z: 6 },
            { x: 2, z: 5 },
            { x: 0, z: 5 },
            { x: 0, z: 6 },
            {
                x: 1,
                z: 6,
                info: {
                    smoke: true,
                    audio: MazeAudio.instance.user4,
                    text: "Uh-oh! The teen is addicted to tobacco and stuck in the maze!",
                    ecig: true,
                },
            },
            { x: 0, z: 6 },
            { x: 0, z: 5 },
            { x: 2, z: 5 },
            { x: 2, z: 6 },
            { x: 4, z: 6 },
            { x: 4, z: 7 },
            { x: 6, z: 7 },
            { x: 6, z: 8 },
            { x: 3, z: 8 },
            { x: 3, z: 7 },
            { x: 1, z: 7 },
        ])
}
